import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useOrdersQuery } from "@/modules/orders/presentation/api/queries";
import { formatDateLocal, formatMonthYearLao } from "@/shared/lib/date-time";
import { formatPriceLak } from "@/shared/lib/format-price";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  Award,
  ChevronRightIcon,
  MailIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  ShoppingBagIcon,
  TagsIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import {
  useAssignCustomerToGroup,
  useCustomerGroupsForCustomerQuery,
  useCustomerGroupsQuery,
  useCustomerQuery,
  useRemoveCustomerFromGroup,
} from "../api/queries";
import { CustomerEditDialog } from "../ui/CustomerEditDialog";

const STATUS_LABELS: Record<string, string> = {
  pending_payment: "ລໍຖ້າຊຳລະ",
  confirmed: "ຢືນຢັນ",
  packing: "ກຳລັງແພັກ",
  shipped: "ສົ່ງແລ້ວ",
  completed: "ສຳເລັດ",
  return_in_transit: "ຕີກັບກຳລັງມາ",
  returned: "ຕີກັບ",
  cancelled: "ຍົກເລີກ",
};

const ORDER_LIMIT = 5;

export function CustomerDetailPage() {
  const { id } = useParams({ from: "/app/customers/$id" });
  const nav = useNavigate();
  const customer = useCustomerQuery(id!);
  const canUpdate = useActionPermission(["customers:update"]);

  const [orderOffset, setOrderOffset] = useState(0);

  const orders = useOrdersQuery({
    limit: ORDER_LIMIT,
    offset: orderOffset,
    sort: [{ field: "createdAt", dir: "desc" }],
    filters: id ? [{ field: "customerId", op: "eq", value: id }] : undefined,
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addingGroupId, setAddingGroupId] = useState<string>("");

  const customerGroups = useCustomerGroupsForCustomerQuery(id!);
  const allGroups = useCustomerGroupsQuery({ limit: 200 });
  const assignGroup = useAssignCustomerToGroup();
  const removeGroup = useRemoveCustomerFromGroup();

  const assignedGroupIds = new Set(
    customerGroups.data?.map((g) => g.groupId) ?? [],
  );
  const availableGroups = (allGroups.data?.data ?? []).filter(
    (g) => !assignedGroupIds.has(g.id),
  );

  const handleAssignGroup = async () => {
    if (!addingGroupId || !id) return;
    await toast.promise(
      assignGroup.mutateAsync({ customerId: id, groupId: addingGroupId }),
      { loading: "ກຳລັງເພີ່ມ...", success: "ເພີ່ມກຸ່ມສຳເລັດ", error: "ເພີ່ມລົ້ມເຫຼວ" },
    );
    setAddingGroupId("");
  };

  const handleRemoveGroup = async (groupId: string) => {
    if (!id) return;
    await toast.promise(
      removeGroup.mutateAsync({ customerId: id, groupId }),
      { loading: "ກຳລັງລຶບ...", success: "ລຶບອອກຈາກກຸ່ມສຳເລັດ", error: "ລຶບລົ້ມເຫຼວ" },
    );
  };

  const c = customer.data;
  const orderData = orders.data?.data ?? [];
  const orderTotal = orders.data?.meta?.total ?? 0;

  // Location from metadata (if available)
  const location =
    (c?.metadata as { address?: string; location?: string })?.address ??
    (c?.metadata as { address?: string; location?: string })?.location;

  if (customer.isLoading || !c) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ລູກຄ້າ", to: "/app/customers" },
              { label: id ?? "ລາຍລະອຽດ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => nav({ to: "/app/customers" })}
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
            <div className="text-muted-foreground">
              {customer.isLoading ? "ກຳລັງໂຫລດ..." : "ບໍ່ພົບລູກຄ້າ"}
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລູກຄ້າ", to: "/app/customers" },
            { label: c.name ?? c.email ?? id ?? "ລາຍລະອຽດ" },
          ]}
        />
      </Header>
      <Main>
        {/* Profile Card */}
        <div className="mb-6 overflow-hidden rounded-xl border bg-card">
          <div className="flex flex-col gap-6 p-6 md:flex-row md:items-start md:justify-between">
            {/* Left: Avatar + Info */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
              <div className="size-20 shrink-0 overflow-hidden rounded-full bg-muted sm:size-24">
                {c.image ? (
                  <AppImage
                    src={c.image}
                    alt={c.name ?? ""}
                    className="size-full object-cover"
                    fallback={
                      <div className="flex size-full items-center justify-center">
                        <UserIcon className="h-10 w-10 text-muted-foreground sm:h-12 sm:w-12" />
                      </div>
                    }
                  />
                ) : (
                  <div className="flex size-full items-center justify-center">
                    <UserIcon className="h-10 w-10 text-muted-foreground sm:h-12 sm:w-12" />
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-bold text-2xl tracking-tight">
                    {c.name ?? c.email ?? "ລູກຄ້າ"}
                  </h2>
                  <Badge
                    variant="secondary"
                    className="rounded px-2 py-0 font-medium text-xs uppercase"
                  >
                    ໃຊ້ງານຢູ່
                  </Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
                  {c.email && (
                    <span className="flex items-center gap-1.5">
                      <MailIcon className="h-4 w-4" />
                      {c.email}
                    </span>
                  )}
                  {c.email && c.phoneNumber && (
                    <span className="text-muted-foreground/50">•</span>
                  )}
                  {c.phoneNumber && (
                    <span className="flex items-center gap-1.5">
                      <PhoneIcon className="h-4 w-4" />
                      {c.phoneNumber}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm">
                  ເປັນລູກຄ້າຕັ້ງແຕ່ {formatMonthYearLao(c.createdAt)}
                </p>
              </div>
            </div>

            {/* Right: Change Tier */}
            <div className="flex shrink-0 flex-col items-end gap-2 space-y-1">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => nav({ to: "/app/customers" })}
                  className="-ml-2"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  ກັບຄືນ
                </Button>
                {canUpdate && (
                  <Button
                    size="default"
                    onClick={() => setEditDialogOpen(true)}
                    className="w-full sm:w-auto"
                  >
                    <PencilIcon className="h-4 w-4" />
                    ປ່ຽນລະດັບສະມາຊິກ
                  </Button>
                )}
              </div>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ສະຖານະປະຈຸບັນ: {c.memberTierName ?? "ບໍ່ກຳນົດ"}
              </p>
            </div>
          </div>

          {/* Attribute Chips */}
          <div className="flex flex-wrap gap-2 border-t bg-muted/30 px-6 py-4">
            {c.memberTierName && (
              <span className="flex items-center gap-2 rounded-lg border bg-background px-3 py-1.5 text-sm">
                <Award className="h-4 w-4 text-muted-foreground" />
                {c.memberTierName}
              </span>
            )}
            {location && (
              <span className="flex items-center gap-2 rounded-lg border bg-background px-3 py-1.5 text-sm">
                <MapPinIcon className="h-4 w-4 text-muted-foreground" />
                {String(location)}
              </span>
            )}
            <span className="flex items-center gap-2 rounded-lg border bg-background px-3 py-1.5 text-sm">
              <ShoppingBagIcon className="h-4 w-4 text-muted-foreground" />
              {c.orderCount ?? 0} ຄຳສັ່ງຊື້ທັງໝົດ
            </span>
          </div>
        </div>

        {/* Customer Groups */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-bold text-lg">
              <TagsIcon className="h-5 w-5 text-muted-foreground" />
              ກຸ່ມລູກຄ້າ
            </h3>
          </div>
          <div className="overflow-hidden rounded-xl border bg-card p-4">
            <div className="flex flex-wrap gap-2">
              {customerGroups.isLoading ? (
                <span className="text-muted-foreground text-sm">ກຳລັງໂຫລດ...</span>
              ) : customerGroups.data?.length === 0 ? (
                <span className="text-muted-foreground text-sm">ຍັງບໍ່ໄດ້ຢູ່ໃນກຸ່ມໃດ</span>
              ) : (
                customerGroups.data?.map((g) => {
                  const n = g.groupName as {
                    lo?: string;
                    th?: string;
                    en?: string;
                  };
                  const label = n?.lo ?? n?.th ?? n?.en ?? g.groupId;
                  return (
                    <span
                      key={g.groupId}
                      className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm"
                      style={
                        g.color
                          ? {
                              borderColor: g.color,
                              backgroundColor: `${g.color}22`,
                              color: g.color,
                            }
                          : undefined
                      }
                    >
                      {label}
                      {canUpdate && (
                        <button
                          type="button"
                          onClick={() => handleRemoveGroup(g.groupId)}
                          className="ml-1 rounded-full p-0.5 hover:bg-black/10"
                          aria-label="ລຶບອອກຈາກກຸ່ມ"
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  );
                })
              )}
            </div>

            {canUpdate && availableGroups.length > 0 && (
              <div className="mt-3 flex items-center gap-2 border-t pt-3">
                <Select
                  value={addingGroupId}
                  onValueChange={setAddingGroupId}
                >
                  <SelectTrigger className="h-8 w-[200px]">
                    <SelectValue placeholder="ເລືອກກຸ່ມ..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableGroups.map((g) => {
                      const n = g.name as {
                        lo?: string;
                        th?: string;
                        en?: string;
                      };
                      return (
                        <SelectItem key={g.id} value={g.id}>
                          {n?.lo ?? n?.th ?? n?.en ?? g.id}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!addingGroupId || assignGroup.isPending}
                  onClick={handleAssignGroup}
                >
                  <PlusIcon className="h-4 w-4" />
                  ເພີ່ມ
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Order History */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-bold text-lg">ປະຫວັດຄຳສັ່ງຊື້</h3>
            <button
              type="button"
              onClick={() =>
                nav({
                  to: "/app/orders",
                  search: {
                    filters: [{ field: "customerId", op: "eq", value: id }],
                  },
                })
              }
              className="flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
            >
              ເບິ່ງທັງໝົດ
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="overflow-hidden rounded-xl border bg-card">
            {orders.isLoading ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                ກຳລັງໂຫລດ...
              </div>
            ) : orderData.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30 text-left">
                        <th className="px-4 py-3 font-medium text-muted-foreground uppercase tracking-wide">
                          ເລກຄຳສັ່ງ
                        </th>
                        <th className="px-4 py-3 font-medium text-muted-foreground uppercase tracking-wide">
                          ວັນທີ
                        </th>
                        <th className="px-4 py-3 font-medium text-muted-foreground uppercase tracking-wide">
                          ສະຖານະ
                        </th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground uppercase tracking-wide">
                          ຍອດລວມ
                        </th>
                        <th className="w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      {orderData.map((o) => (
                        <tr
                          key={o.id}
                          className="cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/50"
                          onClick={() =>
                            nav({ to: "/app/orders/$id", params: { id: o.id } })
                          }
                        >
                          <td className="px-4 py-3 font-mono">#{o.code}</td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {formatDateLocal(o.createdAt)}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant={
                                o.status === "cancelled"
                                  ? "secondary"
                                  : "outline"
                              }
                              className={
                                o.status === "cancelled"
                                  ? "text-muted-foreground"
                                  : ""
                              }
                            >
                              {STATUS_LABELS[o.status] ?? o.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            LAK {formatPriceLak(o.total)}
                          </td>
                          <td className="px-2 py-3">
                            <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {orderTotal > ORDER_LIMIT && (
                  <div className="flex flex-wrap items-center justify-between gap-4 border-t px-4 py-3 text-muted-foreground text-sm">
                    <span>
                      ສະແດງ {orderOffset + 1}–
                      {Math.min(orderOffset + ORDER_LIMIT, orderTotal)} ຈາກ{" "}
                      {orderTotal} ຄຳສັ່ງ
                    </span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setOrderOffset((p) => Math.max(0, p - ORDER_LIMIT))
                        }
                        disabled={orderOffset === 0}
                        className="hover:text-foreground disabled:opacity-50"
                      >
                        ກ່ອນໜ້າ
                      </button>
                      <span className="text-muted-foreground/50">|</span>
                      <button
                        type="button"
                        onClick={() =>
                          setOrderOffset((p) =>
                            orderOffset + ORDER_LIMIT < orderTotal
                              ? p + ORDER_LIMIT
                              : p,
                          )
                        }
                        disabled={orderOffset + ORDER_LIMIT >= orderTotal}
                        className="hover:text-foreground disabled:opacity-50"
                      >
                        ຖັດໄປ
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center text-muted-foreground text-sm">
                ຍັງບໍ່ມີຄຳສັ່ງຊື້
              </div>
            )}
          </div>
        </div>

        <CustomerEditDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          customer={c}
        />
      </Main>
    </>
  );
}
